import { useEffect, useRef } from "react";

/**
 * Una acción que se ejecuta recién cuando pasan `ms` sin volver a llamarla.
 *
 * PARA CONTROLES SIN FIN DE GESTO. Un slider tiene `onValueCommit` —soltar la
 * manija ES el final— y por eso `CampoDeRango` no necesita esto. Escribir no
 * tiene ese evento: nadie avisa que terminó de teclear, así que lo único que
 * queda es esperar a que se detenga.
 *
 * EXISTE PORQUE UNA TECLA COSTABA UNA CONSULTA. El buscador del paso 1 escribía
 * en la url en cada `onChange`, y esa url es parte de la `queryKey` del listado:
 * teclear "Los Maitenes" mandaba doce peticiones y su preflight. Es el mismo
 * defecto que el del slider —un control continuo cableado derecho a la red— con
 * la diferencia de que acá no hay un evento de cierre que usar.
 *
 * LA ACCIÓN SE GUARDA EN UNA REFERENCIA Y SE ACTUALIZA EN CADA RENDER. Sin eso,
 * el temporizador que arrancó hace 300 ms ejecutaría el cierre de aquel render:
 * `cambiarFiltro` parte de `new URLSearchParams(params)`, así que escribiría
 * sobre una url vieja y borraría cualquier otro filtro tocado mientras tanto.
 *
 * @param ms 300 por defecto: por debajo de eso vuelve a dispararse entre teclas
 *           de una misma palabra, y por encima se siente como demora.
 */
export function useAccionDemorada<A extends unknown[]>(
  accion: (...args: A) => void,
  ms = 300,
): (...args: A) => void {
  const vigente = useRef(accion);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    vigente.current = accion;
  });

  // Al desmontar se cancela lo pendiente: un `setParams` sobre una pantalla que
  // ya no está avisa por consola y, peor, navega desde donde nadie lo pidió.
  useEffect(
    () => () => {
      if (temporizador.current !== null) clearTimeout(temporizador.current);
    },
    [],
  );

  return (...args: A) => {
    if (temporizador.current !== null) clearTimeout(temporizador.current);

    temporizador.current = setTimeout(() => {
      temporizador.current = null;
      vigente.current(...args);
    }, ms);
  };
}
